import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserCog, ArrowRight } from "lucide-react";

/**
 * R142 — Impersonation history tile for the admin dashboard.
 *
 * Reads from `GET /api/admin/impersonate/log?limit=5`. Compliance-facing
 * teams love the transparency signal; enterprise-plan customers ask for
 * this on day one.
 */
function relTime(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const STATUS_STYLES = {
  active: "border-emerald-500/40 text-emerald-300",
  ended: "border-white/10 text-white/60",
  expired: "border-amber-500/40 text-amber-300",
};

export default function ImpersonationHistoryCard() {
  const [sessions, setSessions] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/impersonate/log", { params: { limit: 5 } });
        setSessions(data.sessions || []);
        setTotal(data.total_sessions || 0);
      } catch {
        setSessions([]);
      }
    })();
  }, []);

  return (
    <Card className="border-white/10 bg-card p-0 overflow-hidden"
          data-testid="impersonation-history-card">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4 text-orange-400" />
          <div className="text-sm font-semibold">Impersonation history</div>
          {total > 0 && (
            <Badge variant="outline" className="border-white/10 text-white/60 tabular-nums">
              {total} total
            </Badge>
          )}
        </div>
        <Link to="/admin/system/impersonation-log"
              className="text-xs text-orange-300 hover:text-orange-200 flex items-center gap-1"
              data-testid="impersonation-history-see-all">
          See all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {sessions === null ? (
        <div className="p-4 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-9 rounded bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="p-6 text-sm text-white/50 text-center"
             data-testid="impersonation-history-empty">
          No impersonation sessions in the last 30 days. Nice, clean audit trail.
        </div>
      ) : (
        <ul className="divide-y divide-white/5">
          {sessions.map((s) => (
            <li key={s.id || `${s.started_at}-${s.actor_id}`}
                className="px-4 py-2.5 flex items-center gap-3 text-sm"
                data-testid={`impersonation-history-row-${s.id || s.started_at}`}>
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  <span className="text-white">{s.actor_name || "admin"}</span>
                  <span className="text-white/40"> → </span>
                  <span className="text-white/80">{s.target_email || "—"}</span>
                </div>
                {s.reason && (
                  <div className="text-[11px] text-white/50 truncate italic mt-0.5">
                    “{s.reason}”
                  </div>
                )}
              </div>
              <div className="text-[11px] text-white/40 tabular-nums text-right shrink-0">
                {relTime(s.started_at)}
                <div className="text-[10px] text-white/30">{fmtDuration(s.duration_seconds)}</div>
              </div>
              <Badge variant="outline"
                     className={STATUS_STYLES[s.status] || STATUS_STYLES.ended}>
                {s.status}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
